import type { Character } from '../domain/character.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';
import type { CharacterRepository } from '../persistence/characterRepository.ts';
import type { CommitOutcome } from './characterModificationService.ts';
import { batch, commitChanges } from './characterModificationService.ts';
import { equipProblem, entryFor, quantityProblem } from '../rules/inventory.ts';
import type { CharacterChange } from '../domain/characterChange.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { inEncounter } from '../domain/runtime.ts';
import { allocationProblem, removeItemProblem } from '../rules/hotlist.ts';

/**
 * Owning, gaining, losing and wearing things.
 *
 * Every operation is an ordinary `CharacterChange` committed through
 * `characterModificationService`, so inventory is audited, reversible and
 * validated exactly like a Stat edit. There is no inventory-specific save path
 * and no inventory-specific undo.
 *
 * This service holds NO slot arithmetic. `equipProblem` decides whether a Gear
 * Slot has room and produces the sentence the player reads; the change layer's
 * `validateInventoryAgainstCatalogue` is the integrity boundary that actually
 * refuses. Checking here is a courtesy - a readable message instead of a
 * validator's - and the courtesy check calls the same rules function rather
 * than a second copy of the capacity numbers.
 *
 * RULE DCC-INV-013 is enforced HERE, not in the panel. Every operation below
 * goes through one `commit`, and `commit` refuses while an encounter is open.
 * A disabled button explains; a service refusal is what actually holds.
 */

export interface InventoryDeps {
  characters: CharacterRepository;
  /**
   * Items AND Attacks, together. Held occupancy depends on both (RULE
   * DCC-INV-016), so they are never passed separately: a caller with only half
   * a catalogue would be answering the wrong question about a two-handed
   * weapon.
   */
  catalogue: InventoryCatalogue;
  now: () => string;
  /**
   * The crawler's CURRENT runtime, so this service can answer DCC-INV-013 for
   * itself instead of trusting whoever called it. Checked to belong to the
   * crawler being modified: somebody else's quiet runtime is not evidence that
   * THIS crawler is out of combat.
   *
   * Deliberately the whole RuntimeState and not an `inEncounter: boolean`.
   * A boolean is a request; a RuntimeState is the fact. A caller that wanted to
   * slip a gear change into a fight would have to fabricate a runtime whose
   * `encounter` is null while a real one is open, which is not something anyone
   * does by accident and is obvious in review. Required, not optional, so a
   * caller cannot bypass the rule by simply not mentioning it.
   *
   * The priced pathways do NOT call these operations with a runtime that
   * claims no fight is happening. They have their own entry points -
   * `hotlistUseService` for Ready / Swap, `weaponSwap` for the free Attack swap
   * - because the printed rules price every in-combat gear change and this
   * service has no way to charge for one.
   */
  runtime: RuntimeState;
}

/** A refusal the player can read, or null when the operation is allowed. */
export type Refusal = { ok: false; message: string };

function refuse(message: string): Refusal {
  return { ok: false, message };
}

export type InventoryOutcome = CommitOutcome | Refusal;

/**
 * THE gate every ordinary manual inventory operation passes through.
 *
 * Every ordinary inventory operation reaches the disk through this one
 * function, and the
 * DCC-INV-013 check sits here rather than in each of them, so there is one
 * implementation of the rule and no way to add a sixth operation that forgets
 * it. Anything that refuses earlier - a full Gear Slot, an Item the crawler
 * does not own - has refused without changing anything, so no mutation can
 * reach a saved crawler during a fight by any route.
 *
 * A React component disabling a button is NOT this boundary. The panel greys
 * its controls out so the player is told why; this refuses so the rule holds
 * for every caller, including tests, future screens and anything scripted.
 */
async function commit(
  character: Character,
  reason: string,
  changes: CharacterChange[],
  deps: InventoryDeps,
): Promise<InventoryOutcome> {
  // Whose runtime is this? A RuntimeState is only evidence about the crawler it
  // belongs to. Handed somebody else's - a stale one left over from a screen
  // that switched crawlers, say - the encounter check below would be answering
  // a question about the wrong person, and a quiet runtime would wave a change
  // through while THIS crawler is mid-fight. Refused as an internal fault,
  // deliberately worded as one: nothing about the operation itself was illegal,
  // and telling the player their equipment change was against the rules would
  // be a lie about their crawler.
  if (deps.runtime.characterId !== character.id) {
    return refuse(
      `Internal error: this inventory change is for ${character.id} but the runtime supplied ` +
        `belongs to ${deps.runtime.characterId}. Nothing has been changed. Reopen the crawler ` +
        'and try again; if it keeps happening, report it.',
    );
  }

  const locked = inventoryLockedByEncounter(inEncounter(deps.runtime));
  if (locked !== null) return refuse(locked);

  return commitChanges(
    character,
    batch('manual-edit', reason, changes),
    { characters: deps.characters, now: deps.now },
    { catalogue: deps.catalogue },
  );
}

/**
 * Gains copies of an Item.
 *
 * Adding something already owned raises its quantity rather than creating a
 * second entry: quantity IS ownership, and two entries for one ID would be two
 * answers to "how many do I have". `add-item` already merges, so this is only
 * the check and the wording.
 *
 * Newly added copies are never equipped automatically. Putting something on is
 * a separate decision, and in an encounter it is one the rules price
 * (DCC-INV-013).
 */
export async function addItem(
  character: Character,
  itemId: string,
  quantity: number,
  deps: InventoryDeps,
): Promise<InventoryOutcome> {
  const item = deps.catalogue.lookupItem(itemId);
  if (!item) return refuse(`There is no Item with the ID "${itemId}".`);
  if (!Number.isInteger(quantity) || quantity < 1) {
    return refuse('Add a whole number of at least 1.');
  }
  const existing = entryFor(character.inventory, itemId);
  const reason = existing ? `Gained ${quantity} more ${item.name}` : `Gained ${item.name}`;
  return commit(character, reason, [{ kind: 'add-item', itemId, quantity }], deps);
}

/**
 * Sets how many copies are owned, keeping the equipped count.
 *
 * RULE DCC-INV-014 - reducing below the equipped count is REFUSED, never
 * resolved by silently taking something off. "Three helmets, wearing two" may
 * become "two, wearing two"; it may not become "one, wearing one" without the
 * player choosing to unequip. Quietly unequipping would remove a bonus they are
 * still counting on, in the middle of whatever made them adjust the number.
 */
export async function setQuantity(
  character: Character,
  itemId: string,
  quantity: number,
  deps: InventoryDeps,
): Promise<InventoryOutcome> {
  const problem = quantityProblem(character.inventory, itemId, quantity, deps.catalogue.lookupItem);
  if (problem !== null) return refuse(problem);

  const entry = entryFor(character.inventory, itemId);
  if (!entry) return refuse('You do not have that Item.');
  const name = deps.catalogue.lookupItem(itemId)?.name ?? itemId;

  // THE ALLOCATION INVARIANT, from the other side. Every copy is a physical
  // object in one place, so `equipped + held + hotlisted <= owned` has to survive a
  // quantity being lowered as well as a Hotlist slot being filled. Refusing
  // here is the conservative half of the pair: the alternative would be
  // silently unequipping the crawler's armour or shrinking a Hotlist slot they
  // did not ask to change.
  const allocation = allocationProblem(
    character.hotlist,
    itemId,
    name,
    quantity,
    entry.equipped,
    entry.held,
  );
  if (allocation !== null) return refuse(allocation);

  return commit(
    character,
    `${name} set to ${quantity}`,
    [
      {
        kind: 'set-item',
        itemId,
        quantity,
        equipped: entry.equipped,
        // RULE DCC-INV-018 - PRESERVED, not reset. Changing how many the
        // crawler owns says nothing about what is in their hands, and writing
        // 0 here would silently empty them.
        held: entry.held,
      },
    ],
    deps,
  );
}

/**
 * Loses an Item entirely, equipped copies included.
 *
 * Destructive and explicit. The interface says so before asking; this does not
 * soften it by unequipping or stowing first, which would only hide the
 * consequence behind an extra step. The inverse restores quantity, equipped AND
 * held exactly.
 *
 * The legality question is `removeItemProblem`, SHARED with the panel, so the
 * button and the service cannot disagree about the same Item.
 */
export async function removeItem(
  character: Character,
  itemId: string,
  deps: InventoryDeps,
): Promise<InventoryOutcome> {
  const entry = entryFor(character.inventory, itemId);
  if (!entry) return refuse('You do not have that Item.');
  const name = deps.catalogue.lookupItem(itemId)?.name ?? itemId;

  // Losing every copy while a Hotlist slot still allocates some is refused
  // rather than quietly emptying the slot. The player is told to clear the
  // shortcut first, so the Hotlist is never edited by something that was not
  // asked to edit it.
  const allocation = removeItemProblem(character.hotlist, itemId, name);
  if (allocation !== null) return refuse(allocation);

  return commit(character, `Lost all ${name}`, [{ kind: 'remove-item', itemId }], deps);
}

/**
 * Puts ONE copy on. RULE DCC-INV-002 - only equipped Items give bonuses.
 *
 * The capacity question is asked of `equipProblem`, which is the same function
 * the interface uses to grey a button out. The change layer refuses again
 * afterwards; that second refusal is the integrity boundary, and this one only
 * exists so the player gets a sentence rather than a validator message.
 */
export async function equipOne(
  character: Character,
  itemId: string,
  deps: InventoryDeps,
): Promise<InventoryOutcome> {
  const problem = equipProblem(character.inventory, itemId, deps.catalogue);
  if (problem !== null) return refuse(problem);

  const entry = entryFor(character.inventory, itemId);
  if (!entry) return refuse('You do not have that Item.');
  const name = deps.catalogue.lookupItem(itemId)?.name ?? itemId;

  // THE ALLOCATION INVARIANT, from the third side. `equipProblem` answers a
  // Gear Slot question and only ever sees the Inventory, so it cannot know that
  // the last free copy is already allocated to a Hotlist slot. Without this,
  // owned 1 / equipped 0 / hotlisted 1 would happily become owned 1 /
  // equipped 1 / hotlisted 1 - one physical object in two places, which is the
  // exact state the counts were added to make impossible.
  //
  // The same shared rule as `setQuantity`, with the count this Equip WOULD
  // produce, rather than arithmetic written out again here.
  const allocation = allocationProblem(
    character.hotlist,
    itemId,
    name,
    entry.quantity,
    entry.equipped + 1,
    entry.held,
  );
  if (allocation !== null) return refuse(allocation);

  return commit(
    character,
    `Equipped ${name}`,
    [
      {
        kind: 'set-item',
        itemId,
        quantity: entry.quantity,
        equipped: entry.equipped + 1,
        held: entry.held,
      },
    ],
    deps,
  );
}

/** Takes ONE copy off. */
export async function unequipOne(
  character: Character,
  itemId: string,
  deps: InventoryDeps,
): Promise<InventoryOutcome> {
  const entry = entryFor(character.inventory, itemId);
  if (!entry) return refuse('You do not have that Item.');
  const name = deps.catalogue.lookupItem(itemId)?.name ?? itemId;
  if (entry.equipped <= 0) return refuse(`You are not wearing or holding a ${name}.`);

  return commit(
    character,
    `Unequipped ${name}`,
    [
      {
        kind: 'set-item',
        itemId,
        quantity: entry.quantity,
        equipped: entry.equipped - 1,
        held: entry.held,
      },
    ],
    deps,
  );
}

/**
 * RULE DCC-INV-018 - puts a LOOSELY CARRIED copy back into the Inventory.
 *
 * Only `held` copies. Taking off worn gear or sheathing a wielded weapon is
 * `unequipOne`; this is for the helmet, potion or rope a crawler picked up out
 * of the Hotlist and no longer wants in their fist.
 *
 * **OUT OF COMBAT ONLY, and not because it is convenient.** No printed rule
 * gives a crawler a free mid-fight "put this away": Rules p.47 requires
 * physically holding an item for four seconds to store it, which is most of a
 * round, and inventing a free stow would hand back the timing the Action
 * economy is meant to cost. Outside a fight there is no Action economy running,
 * so the ordinary storage interval is abstracted exactly as every other
 * out-of-combat inventory operation abstracts it.
 *
 * In a fight a loose Item leaves the hand through a pathway the rules DO price:
 * the paid Hotlist exchange, or consuming it where it has an authored use.
 * Ground/drop is printed (C&C p.15) and remains unbuilt.
 *
 * Ownership is untouched: stowing is not losing.
 */
export async function stowHeld(
  character: Character,
  itemId: string,
  deps: InventoryDeps,
): Promise<InventoryOutcome> {
  const entry = entryFor(character.inventory, itemId);
  if (!entry) return refuse('You do not have that Item.');
  const name = deps.catalogue.lookupItem(itemId)?.name ?? itemId;
  if (entry.held <= 0) return refuse(`You are not carrying a loose ${name}.`);

  return commit(
    character,
    `Stowed ${name}`,
    [
      {
        kind: 'set-item',
        itemId,
        quantity: entry.quantity,
        equipped: entry.equipped,
        held: entry.held - 1,
      },
    ],
    deps,
  );
}

/**
 * RULE DCC-INV-013 - why gear cannot be changed during an encounter.
 *
 * Rules p.48 gives exactly three ways to change what a crawler is holding, and
 * every one of them is priced:
 *
 *  - activating something from the Hotlist costs an Action;
 *  - swapping an item in your hands for one on the Hotlist happens as part of
 *    the Use a Hotlist Item Action;
 *  - swapping an equipped WEAPON for another weapon on the Hotlist is free, but
 *    only as part of the Attack Action.
 *
 * Nothing in the supplied books lets a crawler re-equip freely mid-fight, and
 * nothing describes changing armour in combat at all. Offering these buttons
 * during a fight would hand out a free swap the printed rules do not allow -
 * inventing a timing rule to make an interface work, which is the one thing
 * this project does not do.
 *
 * The priced routes ARE built and charge what the rules charge: the free
 * weapon swap as part of an Attack (E3) and the Ready / Swap Action (E5). What
 * is still missing is Retrieve - reaching into the Inventory itself - which is
 * exactly why THESE operations stay locked rather than gaining a cost.
 *
 * So FREE inventory management is an out-of-combat activity. The priced
 * pathways now exist and have their own controls: the free Attack weapon swap
 * (E3) and the paid Ready / Swap Action (E5). What is still missing is
 * Retrieve - reaching into the Inventory itself - which is why these ordinary
 * operations stay locked rather than gaining an Action cost.
 *
 * ONE implementation, two audiences: `commit` above calls it to REFUSE, and the
 * panel calls it to explain why its controls are grey. When Retrieve is built
 * it gets its OWN priced operation - do not add a way to tell the ordinary
 * operations to ignore this.
 */
export function inventoryLockedByEncounter(duringEncounter: boolean): string | null {
  return duringEncounter
    ? 'Gear cannot be changed freely during a fight, and putting something away takes four ' +
        'seconds (Rules p.47). The rules-priced combat pathways have their own controls: swap a ' +
        'weapon free as part of an Attack, or use Ready / Swap on the Hotlist for one Action ' +
        '(Rules p.19, p.48). Reaching into the Inventory itself is the Retrieve Action, which ' +
        'is not automated yet. End the encounter to manage equipment normally.'
    : null;
}
